import './ExpandableHeaderGroup.scss';

import { ColumnGroup, IHeaderGroupParams } from '@amzn/gft-ag-grid-enterprise';
import LeftChevron from '@mui/icons-material/ChevronLeft';
import RightChevron from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useState } from 'react';

/**
 * This component is used to display a custom header group that can be expanded/collapsed.
 * The icon reflects the state of the child column group.
 *
 * Example usage is expandable Dimension Headers in Page view:
 *  {
 *    headerName: isDimensionVisible ? `Hide Dimensions` : `Show Dimensions`,
 *    headerGroupComponent: ExpandableHeaderGroup,
 *    children: [
 *     {
 *       headerName: '', // Intentional empty header to support placement of Hide/Show Dimension label and chevron
 *       openByDefault: false,
 *       groupId: 'expandableColumn', // For state persistence
 *       children: generateDimensionHeaders(),
 *      },
 *    ],
 *  }
 *
 * @param props - The properties for the ExpandableHeaderGroup component.
 * @returns The rendered ExpandableHeaderGroup component.
 */
const ExpandableHeaderGroup = (props: IHeaderGroupParams) => {
  const { displayName, columnGroup, api: gridApi } = props;

  // Gets the child column group for expansion (the one defined with the 'openByDefault' property)
  const children = columnGroup.getChildren();
  const expandableChild = children?.[0] as ColumnGroup;
  const [isExpanded, setIsExpanded] = useState(expandableChild?.isExpanded() ?? false);

  /**
   * Handles the click event to expand or collapse the column group.
   */
  const handleExpandCollapse = () => {
    if (expandableChild && gridApi) {
      const newExpansionState = !isExpanded;
      const groupId = expandableChild.getGroupId();
      if (groupId) {
        // Update the column group expansion visually and in local state
        gridApi.setColumnGroupOpened(groupId, newExpansionState);
        setIsExpanded(newExpansionState);
      }
    }
  };

  return (
    <Box onClick={handleExpandCollapse} className='ag-header-group-cell-label ag-sticky-label' role='presentation'>
      <Box component='span' className='ag-header-group-text' role='presentation' data-ref='agLabel'>
        {displayName}
      </Box>
      <Box
        component='span'
        className={`ag-header-icon ag-header-expand-icon ${isExpanded ? 'ag-header-expand-icon-expanded' : 'ag-header-expand-icon-collapsed'}`}
        role='presentation'
        data-ref={isExpanded ? 'agOpened' : 'agClosed'}
        aria-hidden={!isExpanded}
      >
        <IconButton disableRipple disableFocusRipple disableTouchRipple>
          {isExpanded ? <LeftChevron /> : <RightChevron />}
        </IconButton>
      </Box>
    </Box>
  );
};

export default ExpandableHeaderGroup;
